import React, { useState } from "react";
import { db } from "../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  limit as qLimit,
  Timestamp,
} from "firebase/firestore";

const key3 = (r) => `${r.SerialNumber}::${r.ProductId}::${r.Special}`;

const StocktakeToDuplicate = () => {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const clearCollection = async (collPath) => {
    const collRef = collection(db, collPath);
    let total = 0;
    while (true) {
      const snap = await getDocs(query(collRef, qLimit(500)));
      if (snap.empty) break;
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(doc(db, collPath, d.id)));
      await batch.commit();
      total += snap.size;
      setStatus(`Cleared ${total} from ${collPath}…`);
      await new Promise((r) => setTimeout(r, 0));
    }
    return total;
  };

  const buildDuplicate = async () => {
    if (
      !window.confirm(
        "This will DELETE everything in 'cylinders_warehouse_duplicate' and rebuild it from 'stocktake_scanned', while preserving live allocations where possible. Continue?"
      )
    ) {
      return;
    }

    setBusy(true);
    setStatus("Loading stocktake scans…");

    try {
      // 1) Load stocktake scans
      const scansSnap = await getDocs(collection(db, "stocktake_scanned"));
      const scans = scansSnap.docs.map((d) => {
        const data = d.data() || {};
        return {
          id: d.id,
          SerialNumber: data.SerialNumber || "",
          Special: (data.Special ?? "").toString(),
          ProductId: data.ProductId || "",
          LocationId: data.LocationId || "",
          ScannedTime:
            data.ScannedTime && typeof data.ScannedTime.toDate === "function"
              ? data.ScannedTime
              : Timestamp.now(),
        };
      });
      setStatus(`Loaded ${scans.length} scans.`);

      // Group scans by 3-key (Serial, ProductId, Special)
      const scansByKey = new Map();
      for (const s of scans) {
        const k = key3(s);
        const arr = scansByKey.get(k) || [];
        arr.push(s);
        scansByKey.set(k, arr);
      }

      // 2) Load LIVE warehouse to derive allocation/dispatch counts per 3-key
      setStatus("Loading LIVE allocation/dispatch counts…");
      const liveSnap = await getDocs(collection(db, "cylinders_warehouse"));
      const liveCounts = new Map();
      for (const d of liveSnap.docs) {
        const r = d.data() || {};
        const k = key3({
          SerialNumber: r.SerialNumber || "",
          ProductId: r.ProductId || "",
          Special: (r.Special ?? "").toString(),
        });
        const cur = liveCounts.get(k) || { alloc: 0, disp: 0, total: 0 };
        cur.total += 1;
        if (r.Allocated) cur.alloc += 1;
        if (r.Dispatch) cur.disp += 1;
        liveCounts.set(k, cur);
      }

      // 3) Wipe duplicate
      setStatus("Clearing cylinders_warehouse_duplicate…");
      await clearCollection("cylinders_warehouse_duplicate");
      setStatus("Building duplicate from scans…");

      // 4) Rebuild duplicate from scans, applying allocation/dispatch counts
      let written = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const [k, group] of scansByKey.entries()) {
        const counts = liveCounts.get(k) || { alloc: 0, disp: 0, total: 0 };
        let remainingAlloc = counts.alloc;
        let remainingDisp = counts.disp;

        // deterministic order: newest first (optional)
        group.sort((a, b) => {
          const ta = a.ScannedTime?.seconds || 0;
          const tb = b.ScannedTime?.seconds || 0;
          return tb - ta;
        });

        for (const s of group) {
          const allocated = remainingAlloc > 0;
          const dispatch = remainingDisp > 0;
          if (remainingAlloc > 0) remainingAlloc--;
          if (remainingDisp > 0) remainingDisp--;

          const isStock = s.Special === "50";

          const docData = {
            ProductId: s.ProductId,
            Special: s.Special,
            SerialNumber: s.SerialNumber,
            LocationId: s.LocationId,
            Allocated: allocated,
            Stock: isStock,
            Dispatch: dispatch,
            currentlyEditingBy: null,
            BookedInTime: s.ScannedTime || Timestamp.now(),
          };

          const newRef = doc(collection(db, "cylinders_warehouse_duplicate"));
          batch.set(newRef, docData);
          batchCount++;
          written++;

          if (batchCount === 500) {
            await batch.commit();
            setStatus(`Wrote ${written} docs to duplicate…`);
            batch = writeBatch(db);
            batchCount = 0;
            await new Promise((r) => setTimeout(r, 0));
          }
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      setStatus(`Done. Wrote ${written} documents to cylinders_warehouse_duplicate.`);
    } catch (err) {
      console.error(err);
      setStatus("Error while rebuilding duplicate. Check console.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 max-w-xl mx-auto">
      <h2 className="text-xl font-semibold mb-3">Stocktake → Duplicate (preserve allocations)</h2>
      <p className="text-sm text-gray-700 mb-4">
        Builds <code>cylinders_warehouse_duplicate</code> from <code>stocktake_scanned</code>.
        Allocation/dispatch are carried over from LIVE (<code>cylinders_warehouse</code>) by the key{" "}
        <code>SerialNumber + ProductId + Special</code>. Duplicate is cleared first.
      </p>
      <button
        onClick={buildDuplicate}
        disabled={busy}
        className={`w-full py-3 rounded-md text-white ${
          busy ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
        }`}
      >
        {busy ? "Working…" : "Rebuild duplicate from scans"}
      </button>
      {status && <p className="mt-3 text-sm text-gray-700">{status}</p>}
      <div className="mt-2 text-xs text-gray-500">
        Notes:
        <ul className="list-disc list-inside">
          <li>Duplicate is wiped and repopulated from scans only.</li>
          <li>If LIVE has more allocated than scans for a key, we mark all scanned as allocated.</li>
          <li>Location comes from the scan; <code>Stock</code> = <code>Special === "50"</code>.</li>
          <li><code>BookedInTime</code> uses scan time; <code>currentlyEditingBy</code> is reset.</li>
        </ul>
      </div>
    </div>
  );
};

export default StocktakeToDuplicate;
